import {
  Box,
  Button,
  Container,
  Link,
  MenuItem,
  Typography,
} from '@mui/material'
import React, {
  useCallback,
  useState,
  useEffect,
} from 'react'
import {
  Link as RouterLink,
  useNavigate,
} from 'react-router-dom'
import { useDispatch } from 'react-redux'

import { api } from '@common-sense-privacy/common'

import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'

import Form from '@/components/Form'
import FormField from '@/components/FormField'

import { useGetProductTypesQuery } from '@/services/productTypes'
import { useGetCategoriesQuery } from '@/services/categories'
import { usePostCreateProductMutation } from '@/services/organization/product'
import { useGetDistributionMethodsQuery } from '@/services/distributionMethods'

import { useCurrentOrganization } from '@/hooks/useSession'
import {
  setProductInfo,
  useProductInfo,
} from '@/hooks/useProductInformation'

import useHandleFormApiErrors from '@/hooks/useHandleFormApiErrors'

import DocumentWrapper from '../../../../components/DocumentWrapper'
import ScreenWrapper from '../../../../components/ScreenWrapper'

import Loading from '../../components/Loading'

import type {
  FormValues,
  OnSubmit,
} from './types'

function ProductInformation(): React.ReactElement {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id: organizationId } = useCurrentOrganization()
  const handleFormApiErrors = useHandleFormApiErrors()

  const {
    data: productTypes,
    isLoading: isProductTypesLoading,
  } = useGetProductTypesQuery(undefined, { refetchOnMountOrArgChange: true })

  const {
    data: categories,
    isLoading: isCategoriesLoading,
  } = useGetCategoriesQuery(undefined, { refetchOnMountOrArgChange: true })

  const [
    postCreateProduct,
    { isLoading: isCreatingProduct },
  ] = usePostCreateProductMutation({ fixedCacheKey: 'shared-creating-product' })

  const {
    data: distributionMethods,
    isLoading: isDistributionMethodsLoading,
  } = useGetDistributionMethodsQuery(undefined, { refetchOnMountOrArgChange: true })

  const initialValues = useProductInfo()

  const [
    selectedProductType,
    setSelectedProductType,
  ] = useState('')

  useEffect(() => {
    if (productTypes && !selectedProductType && initialValues.type) {
      setSelectedProductType(productTypes.data
        ?.find(productType => productType.id === initialValues.type)?.name || '')
    }
  }, [
    productTypes,
    selectedProductType,
    setSelectedProductType,
    initialValues.type,
  ])

  const { rules } = api.endpoints.organization.product.validation.post.body
  const formRules = { ...rules }

  const handleSubmit = useCallback<OnSubmit>(async (values: FormValues, { setErrors }) => {
    postCreateProduct({
      body: {
        name: values.name,
        type: productTypes?.data?.find(type => type.name === values.type)?.id || '',
        categories: values.categories,
        distributionMethods: values.type === 'Mobile App' ? values.distributionMethods : undefined,
        privacyPolicyIntroduction: values.privacyPolicyIntroduction,
      },
      params: { id: organizationId },
    }).unwrap().then(() => {
      dispatch(setProductInfo(values))
      navigate('/wizard/start')
    }).catch(error => {
      handleFormApiErrors({
        error,
        setErrors,
        showFieldErrorsAsToast: true,
      })
    })
  }, [
    dispatch,
    handleFormApiErrors,
    navigate,
    organizationId,
    postCreateProduct,
    productTypes?.data,
  ])

  return (
    <DocumentWrapper title='Common Sense Privacy | Product Information'>
      <ScreenWrapper>
        {isCreatingProduct ? <Loading />
          : (
            <>
              <Container>
                <Box>
                  <Link
                    component={RouterLink}
                    variant='body2'
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'center',
                      color: 'text.primary',
                      textDecoration: 'none',
                      fontWeight: 'bold',
                    }}
                    to='/subscriptions/plans'
                  >
                    <KeyboardArrowLeftIcon /> Back
                  </Link>
                </Box>
              </Container>
              <Container maxWidth='sm'>
                <Typography variant='h5' textAlign='center' mb={4}>Tell us about your product...</Typography>
                <Typography variant='body1' mb={5} textAlign='center'>Enter your product name and details to get started.
                </Typography>
                <Form<FormValues> initialValues={initialValues} onSubmit={handleSubmit} rules={formRules} marginBottom={6} spacing={4} alignItems='center'>
                  <FormField
                    name='name'
                    label='Product Name'
                    formControlProps={{ fullWidth: true }}
                    inputProps={{ 'data-testid': 'product-name' }}
                  />
                  <FormField
                    name='type'
                    type='select'
                    labelId='product-type-select-label'
                    id='product-type-select'
                    label='Product Type'
                    formControlProps={{ fullWidth: true }}
                    data-testid='product-type-wrapper'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSelectedProductType(e.target.value)}
                  >
                    {(productTypes?.data || []).map(productType => (
                      <MenuItem
                        key={productType.id}
                        value={productType.name}
                      >
                        {productType.name}
                      </MenuItem>
                    ))}
                  </FormField>
                  {selectedProductType === 'Mobile App' && (
                    <FormField
                      name='distributionMethods'
                      type='select'
                      multiple={true}
                      label='Product Distribution Methods'
                      labelId='product-distribution-methods-select-label'
                      formControlProps={{ fullWidth: true }}
                      data-testid='product-distribution-methods-wrapper'
                    >
                      {(distributionMethods?.data || []).map(distMethod => (
                        <MenuItem key={distMethod.id} value={distMethod.id}>
                          {distMethod.name}
                        </MenuItem>
                      ))}
                    </FormField>
                  )}
                  <FormField
                    name='categories'
                    type='select'
                    multiple={true}
                    label='Product Categories'
                    labelId='product-industry-select-label'
                    formControlProps={{ fullWidth: true }}
                    data-testid='product-categories-wrapper'
                  >
                    {(categories?.data || []).map(category => (
                      <MenuItem
                        key={category.id}
                        value={category.id}
                      >
                        {category.name}
                      </MenuItem>
                    ))}
                  </FormField>
                  <FormField
                    name='privacyPolicyIntroduction'
                    type='text'
                    multiline={true}
                    maxRows={Infinity}
                    minRows={4}
                    label='Privacy Policy Introduction (Optional)'
                    infoTip='If you wish to add a custom introduction to your privacy policy, enter it here.'
                    formControlProps={{
                      fullWidth: true,
                      sx: { mt: 0 },
                    }}
                  />
                  <Button
                    disabled={
                      isProductTypesLoading || isCategoriesLoading
                      || isCreatingProduct || isDistributionMethodsLoading
                    }
                    role='button'
                    type='submit'
                  >
                    Continue
                  </Button>
                </Form>
              </Container>
            </>
          )}
      </ScreenWrapper>
    </DocumentWrapper>
  )
}

export default React.memo(ProductInformation)
